<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { useTemplateRef, type InputHTMLAttributes } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import { useLocale } from "../../locale";
import Button from "../button/Button.vue";
import { Icon } from "../icon";
import { TextInput } from "../input";
import { type InputProps } from "../input/Input.vue";
import { InputGroup } from "../inputgroup";
import { useDataCollectionStateModel } from "./composables/data-collection-state-model";

interface Props extends ClassProps {
	filter?: string;

	/**
	 * Props that will be forwarded to the inner input
	 */
	inputProps?: InputProps & Omit<InputHTMLAttributes, keyof InputProps>;
}

const props = defineProps<Props>();
const emits = defineEmits<{
	"update:filter": [val: string];
}>();

const { t } = useLocale();

const filter = useDataCollectionStateModel("filter", props, emits);

const inputRef = useTemplateRef("$textInput");

function removeSearch() {
	filter.value = "";

	inputRef.value?.$el.focus();
}
</script>

<template>
	<InputGroup :class="cn('max-w-88 flex w-full items-center', props.class)">
		<TextInput
			ref="$textInput"
			v-model="filter"
			class="grow"
			:placeholder="`${t('table.filter')}...`"
			v-bind="{
				size: 'sm',
				...props.inputProps,
			}"
		/>

		<Button v-if="filter" shape="round" size="xs" variant="ghost" @click="removeSearch">
			<Icon :d="mdiClose" />
		</Button>
	</InputGroup>
</template>
